import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import $i18n from '~/services/i18n';
import useContentCountStore from './contentCountStore';
import useModalStore from '~/stores/modalStore';

const MY_LIBRARY_CONTENT_APPROACHING_LIMIT_DELTA = 5;

// will need to rename as ll manager or something,
const useContentStatusStore = defineStore('contentStatus', () => {
  const exceedThreshold = ref(Number.POSITIVE_INFINITY);
  const store = useStore();

  const {
    publishedCount, archivedCount, lifetimeAccessCount,
  } = storeToRefs(useContentCountStore());
  const modalStore = useModalStore();

  const isEligibleForLimits = computed(() => exceedThreshold.value !== Number.POSITIVE_INFINITY);

  const warnThreshold = computed(() => exceedThreshold.value - MY_LIBRARY_CONTENT_APPROACHING_LIMIT_DELTA);

  const showLimitsIndicator = computed(() => isEligibleForLimits.value);

  const canArchive = computed(() => isEligibleForLimits.value
    && (publishedCount.value > 0 || archivedCount.value > 0));

  const showArchivedTab = computed(() => canArchive.value);

  const showLifetimeAccessTab = computed(() => lifetimeAccessCount.value > 0);

  const shouldWarn = computed(() => publishedCount.value >= warnThreshold.value && publishedCount.value < exceedThreshold.value);

  const shouldRestrict = computed(() => publishedCount.value >= exceedThreshold.value);

  const isOverLibLimit = computed(() => publishedCount.value >= (exceedThreshold.value + 1));

  const remainingLibraryLimit = computed(() => Math.max(exceedThreshold.value - publishedCount.value, 0));

  function setContentLimitExceedThreshold(limit: number) {
    exceedThreshold.value = limit;
  }

  function willNewActivitiesExceedThreshold(newActivityCount: number) {
    return (publishedCount.value + newActivityCount) > exceedThreshold.value;
  }

  function shouldAllowBulkImportingFiles(files: Array<any>) {
    if (willNewActivitiesExceedThreshold(files?.length)) {
      if (store.getters['uiManager/isBulkImportModalShowing']) {
        store.dispatch('uiManager/toggleBulkImport');
      }
      modalStore.showOutOfStorageModal({
        source: 'import-slides-modal',
        mode: 'create',
        customDescription: $i18n(
          'You are trying to import {$1} files but you can only import {$2} more on the basic plan which has a limit of {$3} quizzes and lessons.',
          [files.length, remainingLibraryLimit.value, exceedThreshold.value],
        ),
        restDescriptions: [
          $i18n('Upgrade or free up storage to import more.'),
        ],
      });
      return false;
    }
    return true;
  }

  return {
    isEligibleForLimits,
    exceedThreshold,
    warnThreshold,

    publishedCount,
    archivedCount,
    lifetimeAccessCount,

    showArchivedTab,
    showLifetimeAccessTab,

    showLimitsIndicator,
    canArchive,
    shouldWarn,
    shouldRestrict,
    isOverLibLimit,
    remainingLibraryLimit,

    willNewActivitiesExceedThreshold,
    shouldAllowBulkImportingFiles,

    // will be used to bootstrap store
    setContentLimitExceedThreshold,
  };
});

export default useContentStatusStore;
